import { apiRequest } from '../request'
import type { ApiResponse } from '../request'

export type VideoRoomStatus = 'IDLE' | 'ACTIVE' | 'CLOSED'
export type VideoParticipantRole = 'moderator' | 'participant' | 'viewer'
export type VideoParticipantKind = 'PUBLISHER' | 'VIEWER' | 'RECORDER'

export const VIDEO_FEATURE_LABELS = {
    prejoin: 'Tela de pré-entrada (testar câmera e microfone)',
    screenShare: 'Compartilhar tela',
    chat: 'Chat na sala',
    raiseHand: 'Levantar a mão',
    reactions: 'Reações',
    waitingRoom: 'Sala de espera (o moderador admite)',
} as const

export type VideoFeature = keyof typeof VIDEO_FEATURE_LABELS
export type VideoFeatures = Record<VideoFeature, boolean>

export interface VideoPlanLimits {
    maxParticipants: number | null
    maxConcurrentRooms: number | null
    maxRoomMinutes: number | null
}

export interface VideoRoom {
    id: number
    livekitName: string
    title: string | null
    externalId: string | null
    status: VideoRoomStatus
    maxParticipants: number
    /** Grava sozinha cada sessão (plano com gravação). */
    autoRecord: boolean
    features: Partial<VideoFeatures>
    metadata: Record<string, unknown>
    closedAt: string | null
    createdAt: string
}

export interface VideoRoomList {
    items: VideoRoom[]
    total: number
    page: number
    limit: number
}

export interface VideoToken {
    token: string
    identity: string
    serverUrl: string
    joinUrl: string | null
    expiresAt: string
    room: { id: number; name: string }
}

export interface VideoApiKey {
    id: number
    name: string
    keyId: string
    lastUsedAt: string | null
    revokedAt: string | null
    createdAt: string
}

export interface VideoUsageRow {
    day: string
    kind: VideoParticipantKind
    participants: number
    minutes: number
    credits: number
    uncoveredCredits: number
}

export interface VideoBranding {
    displayName?: string
    logoUrl?: string
    primaryColor?: string
}

export interface VideoSettings {
    allowedOrigins: string[]
    defaultFeatures: VideoFeatures
    branding: VideoBranding
}

export type VideoCustomDomainStatus = 'PENDING_DNS' | 'PENDING_CERT' | 'ACTIVE' | 'FAILED'

export interface VideoDnsRecord {
    type: 'CNAME' | 'TXT'
    name: string
    value: string
    /** false: opcional, só adianta a verificação. */
    required: boolean
}

export interface VideoCustomDomain {
    domain: string | null
    status: VideoCustomDomainStatus | null
    error: string | null
    records: VideoDnsRecord[]
    checkedAt: string | null
    activatedAt: string | null
    availableInPlan: boolean
    platformConfigured: boolean
    cnameTarget: string
}

export interface VideoCreditBalance {
    /** Cobrança de vídeo ligada no MASTER. */
    enabled: boolean
    planCredits: number
    totalCredits: number
    plan: { remainingCredits: number; grantedCredits: number; expiresAt: string } | null
    purchased: Array<{ remainingCredits: number; grantedCredits: number; expiresAt: string }>
    monthUsedCredits: number
    monthUncoveredCredits: number
    rates: { viewerMinuteCredits: number; recordingMinuteCredits: number }
}

export async function getVideoCredits(): Promise<ApiResponse<VideoCreditBalance>> {
    return apiRequest<VideoCreditBalance>('video/credits', {})
}

export async function getVideoPlanLimits(): Promise<ApiResponse<VideoPlanLimits>> {
    return apiRequest<VideoPlanLimits>('video/plan-limits', {})
}

export async function listVideoRooms(params: {
    page?: number
    limit?: number
    status?: VideoRoomStatus
}): Promise<ApiResponse<VideoRoomList>> {
    return apiRequest<VideoRoomList>('video/rooms', { params })
}

export async function createVideoRoom(data: {
    title?: string
    externalId?: string
    maxParticipants?: number
    autoRecord?: boolean
}): Promise<ApiResponse<VideoRoom>> {
    return apiRequest<VideoRoom>('video/rooms', { data }, 'post')
}

export async function issueVideoToken(
    roomId: number,
    data: { name: string; role: VideoParticipantRole },
): Promise<ApiResponse<VideoToken>> {
    return apiRequest<VideoToken>(`video/rooms/${roomId}/tokens`, { data }, 'post')
}

export async function closeVideoRoom(roomId: number): Promise<ApiResponse<VideoRoom>> {
    return apiRequest<VideoRoom>(`video/rooms/${roomId}/close`, {}, 'post')
}

export async function listVideoApiKeys(): Promise<ApiResponse<VideoApiKey[]>> {
    return apiRequest<VideoApiKey[]>('video/api-keys', {})
}

export async function createVideoApiKey(name: string): Promise<ApiResponse<VideoApiKey & { secret: string }>> {
    return apiRequest<VideoApiKey & { secret: string }>('video/api-keys', { data: { name } }, 'post')
}

export async function revokeVideoApiKey(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`video/api-keys/${id}`, {}, 'delete')
}

export async function getVideoUsage(from: string, to: string): Promise<ApiResponse<VideoUsageRow[]>> {
    return apiRequest<VideoUsageRow[]>('video/usage', { params: { from, to } })
}

export async function getVideoSettings(): Promise<ApiResponse<VideoSettings>> {
    return apiRequest<VideoSettings>('video/settings', {})
}

export async function updateVideoSettings(
    data: Partial<VideoSettings>,
): Promise<ApiResponse<VideoSettings>> {
    return apiRequest<VideoSettings>('video/settings', { data }, 'put')
}

export async function getVideoCustomDomain(): Promise<ApiResponse<VideoCustomDomain>> {
    return apiRequest<VideoCustomDomain>('video/custom-domain', {})
}

export async function setVideoCustomDomain(domain: string): Promise<ApiResponse<VideoCustomDomain>> {
    return apiRequest<VideoCustomDomain>('video/custom-domain', { data: { domain } }, 'put')
}

export async function refreshVideoCustomDomain(): Promise<ApiResponse<VideoCustomDomain>> {
    return apiRequest<VideoCustomDomain>('video/custom-domain/refresh', {}, 'post')
}

export async function removeVideoCustomDomain(): Promise<ApiResponse<VideoCustomDomain>> {
    return apiRequest<VideoCustomDomain>('video/custom-domain', {}, 'delete')
}

export interface VideoInvite {
    enabled: boolean
    requiresApproval: boolean
    expiresAt: string | null
    /** Link do convite; null enquanto nunca foi ligado. */
    url: string | null
}

export async function getVideoInvite(roomId: number): Promise<ApiResponse<VideoInvite>> {
    return apiRequest<VideoInvite>(`video/rooms/${roomId}/invite`, {})
}

export async function updateVideoInvite(
    roomId: number,
    data: { enabled?: boolean; requiresApproval?: boolean; expiresAt?: string | null; regenerate?: boolean },
): Promise<ApiResponse<VideoInvite>> {
    return apiRequest<VideoInvite>(`video/rooms/${roomId}/invite`, { data }, 'put')
}

export type VideoRecordingStatus = 'STARTING' | 'RECORDING' | 'PROCESSING' | 'READY' | 'FAILED'

export interface VideoRecording {
    id: number
    roomId: number
    status: VideoRecordingStatus
    sizeBytes: number
    durationSeconds: number
    startedBy: string
    startedAt: string
    endedAt: string | null
    error: string | null
}

export async function listVideoRecordings(roomId: number): Promise<ApiResponse<VideoRecording[]>> {
    return apiRequest<VideoRecording[]>(`video/rooms/${roomId}/recordings`, {})
}

export async function startVideoRecording(roomId: number): Promise<ApiResponse<VideoRecording>> {
    return apiRequest<VideoRecording>(`video/rooms/${roomId}/recordings/start`, {}, 'post')
}

export async function stopVideoRecording(roomId: number): Promise<ApiResponse<VideoRecording | null>> {
    return apiRequest<VideoRecording | null>(`video/rooms/${roomId}/recordings/stop`, {}, 'post')
}

export async function getVideoRecordingDownload(
    id: number,
    mode: 'play' | 'download',
): Promise<ApiResponse<{ url: string; expiresAt: string }>> {
    return apiRequest<{ url: string; expiresAt: string }>(`video/recordings/${id}/download?mode=${mode}`, {})
}

export async function deleteVideoRecording(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`video/recordings/${id}`, {}, 'delete')
}

export const VIDEO_WEBHOOK_EVENT_LABELS = {
    'room.started': 'Reunião aberta',
    'room.ended': 'Reunião encerrada',
    'participant.joined': 'Participante entrou',
    'participant.left': 'Participante saiu',
    'recording.started': 'Gravação começou',
    'recording.ready': 'Gravação pronta',
    'recording.failed': 'Gravação falhou',
} as const

export type VideoWebhookEvent = keyof typeof VIDEO_WEBHOOK_EVENT_LABELS

export interface VideoWebhookEndpoint {
    url: string
    events: VideoWebhookEvent[]
    enabled: boolean
    /** Só no primeiro cadastro. */
    secret?: string
}

export interface VideoWebhookDelivery {
    id: string
    eventId: string
    event: string
    status: 'PENDING' | 'SUCCESS' | 'FAILED'
    attempts: number
    lastStatusCode: number | null
    lastError: string | null
    createdAt: string
    deliveredAt: string | null
    nextAttemptAt: string | null
}

export async function getVideoWebhook(): Promise<ApiResponse<VideoWebhookEndpoint | null>> {
    return apiRequest<VideoWebhookEndpoint | null>('video/webhook', {})
}

export async function saveVideoWebhook(data: {
    url: string
    events: VideoWebhookEvent[]
    enabled: boolean
}): Promise<ApiResponse<VideoWebhookEndpoint>> {
    return apiRequest<VideoWebhookEndpoint>('video/webhook', { data }, 'put')
}

export async function removeVideoWebhook(): Promise<ApiResponse<void>> {
    return apiRequest<void>('video/webhook', {}, 'delete')
}

export async function rotateVideoWebhookSecret(): Promise<ApiResponse<{ secret: string }>> {
    return apiRequest<{ secret: string }>('video/webhook/rotate-secret', {}, 'post')
}

export async function testVideoWebhook(): Promise<ApiResponse<VideoWebhookDelivery>> {
    return apiRequest<VideoWebhookDelivery>('video/webhook/test', {}, 'post')
}

export async function listVideoWebhookDeliveries(): Promise<ApiResponse<VideoWebhookDelivery[]>> {
    return apiRequest<VideoWebhookDelivery[]>('video/webhook/deliveries', {})
}
